import { createSlice } from '@reduxjs/toolkit'
import { createAxiosThunk } from 'utils/reduxUtil'
import ad from 'api/axios/foka/ad'
import recommendKeyword from 'api/axios/foka/recommendKeyword'
import {
  BiddingStrategyTypes,
  CampaignTypes,
  MaxBiddingAmountChangeTypes,
  Ranks,
} from 'features/Codes'
import { uniq } from 'lodash'

const combinationArray = (...arrays) => {
  if (arrays.length === 1) return arrays[0].map(item => [item])
  const [firstArray, ...restArrays] = arrays
  return combinationArray(...restArrays)
    .map(result => firstArray.map(item => [item, ...result]))
    .flat()
}

const combinationName = (...arrays) =>
  combinationArray(...arrays).map(array => array.join(' '))

const removeBrandNamesFromChannelNames = (channelNames, brandNames) => {
  const results = []
  channelNames.forEach(channelName => {
    brandNames.forEach(brandName => {
      const result = channelName
        .replace(brandName, '')
        .replaceAll(/\s{2,}/g, ' ')
        .trim()
      if (result.length > 0) {
        results.push(result)
      }
    })
  })
  return uniq(results)
}

const initialState = {
  isSubmitting: false,
  isChannelNamesLoading: false,
  form: {
    texts: [],
    suggestions: [],
    search: '',
    biddingStrategyType: BiddingStrategyTypes.INHERIT_FROM_ADGROUP,
    maxBiddingAmount: '',
    maxBiddingAmountChangeType: MaxBiddingAmountChangeTypes.NO_CALCULATE,
    rank: Ranks.MO1,
    useRankAmount: false,
    rankAmount: '',
    channelNames: [],
    brandNames: [],
    productNames: [],
    combinations: [],
    asc: true,
  },
  recommends: {
    isLoading: false,
    content: [],
  },
  biddingAverages: {
    visible: false,
    loading: false,
    loaded: false,
    keywords: [],
    rank: Ranks.MO1,
    useRankAmount: false,
    rankAmount: '',
    content: [],
  },
}

export const createKeywords = createAxiosThunk(
  'keyword/createKeywords',
  (biddingAverages = [], { getState }) => {
    const {
      campaign: {
        form: { campaignType },
      },
      adGroup: {
        form: { id: adGroupId },
      },
      keyword: {
        form: {
          texts,
          suggestions,
          biddingStrategyType,
          maxBiddingAmount,
          maxBiddingAmountChangeType,
          rank,
          useRankAmount,
          rankAmount,
        },
      },
    } = getState().ad
    const keywords = [...texts, ...suggestions]
    return ad.postKeywords(campaignType, {
      adGroupId,
      ...(campaignType === CampaignTypes.PREMIUM_LINK && {
        commands: keywords.map(keyword => {
          let fixedMaxBiddingAmount = maxBiddingAmount
          if (
            biddingStrategyType === BiddingStrategyTypes.INHERIT_FROM_ADGROUP
          ) {
            fixedMaxBiddingAmount = null
          } else if (
            biddingStrategyType !== BiddingStrategyTypes.INHERIT_FROM_ADGROUP &&
            maxBiddingAmountChangeType === MaxBiddingAmountChangeTypes.RANK
          ) {
            const biddingAverageAmount = biddingAverages
              ?.find(biddingAverage => biddingAverage.keyword === keyword)
              ?.[rank.device.toLowerCase()]?.find(
                biddingAverage => biddingAverage.rank === rank.rank
              )?.averageBiddingAmount
            fixedMaxBiddingAmount = biddingAverageAmount
              ? useRankAmount &&
                rankAmount !== '' &&
                biddingAverageAmount > rankAmount
                ? rankAmount
                : biddingAverageAmount
              : 70
          }
          return {
            text: keyword,
            biddingStrategy: {
              type: biddingStrategyType,
              ...(fixedMaxBiddingAmount && {
                maxBiddingAmount: fixedMaxBiddingAmount,
              }),
            },
          }
        }),
      }),
      texts: keywords,
    })
  }
)

export const fetchRecommendKeywords = createAxiosThunk(
  'keyword/fetchRecommendKeywords',
  keyword => recommendKeyword.searchKeywords(keyword)
)

export const fetchBiddingAverages = createAxiosThunk(
  'keyword/fetchBiddingAverages',
  ({ keywords }) => recommendKeyword.getBiddingAverages(keywords)
)

export const fetchChannelName = createAxiosThunk(
  'keyword/fetchChannelName',
  (noArgs, { getState }) => {
    const { bizChannelId } = getState().ad.campaign.form
    return ad.getBizChannel(bizChannelId)
  }
)

const keywordSlice = createSlice({
  name: 'keyword',
  initialState,

  reducers: {
    reset: () => initialState,
    setTexts: (state, { payload: texts }) => {
      state.form.texts = texts
    },
    setSearch: (state, { payload: search }) => {
      state.form.search = search
    },
    setBiddingStrategyType: (state, { payload: biddingStrategyType }) => {
      state.form.biddingStrategyType = biddingStrategyType
      state.form.maxBiddingAmountChangeType =
        MaxBiddingAmountChangeTypes.NO_CALCULATE
      state.form.maxBiddingAmount = ''
      state.form.rank = Ranks.MO1
      state.form.useRankAmount = false
      state.form.rankAmount = ''
    },
    setMaxBiddingAmount: (state, { payload: maxBiddingAmount }) => {
      state.form.maxBiddingAmount = maxBiddingAmount
    },
    setMaxBiddingAmountChangeType: (
      state,
      { payload: maxBiddingAmountChangeType }
    ) => {
      state.form.maxBiddingAmountChangeType = maxBiddingAmountChangeType
      state.form.maxBiddingAmount = ''
      state.form.rank = Ranks.MO1
      state.form.useRankAmount = false
      state.form.rankAmount = ''
    },
    setRank: (state, { payload: rank }) => {
      state.form.rank = rank
    },
    setUseRankAmount: (state, { payload: useRankAmount }) => {
      state.form.useRankAmount = useRankAmount
    },
    setRankAmount: (state, { payload: rankAmount }) => {
      state.form.rankAmount = rankAmount
    },
    hideBiddingAverages: state => {
      state.biddingAverages.visible = false
    },
    clear: state => {
      state.form.texts = []
      state.form.suggestions = []
    },
    addSuggestions: (state, { payload: suggestion }) => {
      state.form.suggestions.push(suggestion)
    },
    removeSuggestions: (state, { payload: suggestion }) => {
      const index = state.form.suggestions.indexOf(suggestion)
      if (index !== -1) state.form.suggestions.splice(index, 1)
    },
    checkRecommends: state => {
      state.form.suggestions = state.form.suggestions.concat(
        state.recommends.content
          .filter(
            item =>
              !state.form.suggestions.some(
                suggestion => suggestion === item.keyword
              )
          )
          .map(item => item.keyword)
      )
    },
    uncheckRecommends: state => {
      state.form.suggestions = state.form.suggestions.filter(
        item =>
          !state.recommends.content.some(
            suggestion => suggestion.keyword === item
          )
      )
    },
    setChannelNames: (state, { payload: channelNames }) => {
      state.form.channelNames = channelNames
    },
    setBrandNames: (state, { payload: brandNames }) => {
      state.form.brandNames = brandNames
    },
    setProductNames: (state, { payload: productNames }) => {
      state.form.productNames = productNames
    },
    createCombinations: state => {
      const combinations = [
        ...state.form.channelNames,
        ...state.form.brandNames,
      ]
      if (
        state.form.brandNames.length > 0 &&
        state.form.productNames.length > 0
      ) {
        combinations.push(
          ...combinationName(state.form.brandNames, state.form.productNames)
        )
      }
      if (
        state.form.channelNames.length > 0 &&
        state.form.brandNames.length > 0
      ) {
        const stripChannelNames = removeBrandNamesFromChannelNames(
          state.form.channelNames,
          state.form.brandNames
        )
        combinations.push(...stripChannelNames)
        if (state.form.productNames.length > 0) {
          combinations.push(
            ...combinationName(stripChannelNames, state.form.productNames)
          )
        }
      }
      state.form.combinations = uniq(
        combinations.map(combination => combination.trim())
      ).sort()
      state.form.asc = true
    },
    checkCombinations: state => {
      state.form.suggestions = state.form.suggestions.concat(
        state.form.combinations.filter(
          combination =>
            combination.length <= 50 &&
            !state.form.suggestions.some(
              suggestion => suggestion === combination
            )
        )
      )
    },
    uncheckCombinations: state => {
      state.form.suggestions = state.form.suggestions.filter(
        suggestion =>
          !state.form.combinations
            .filter(combination => combination.length <= 50)
            .some(combination => combination === suggestion)
      )
    },
    sort: state => {
      state.form.combinations = state.form.combinations.reverse()
      state.form.asc = !state.form.asc
    },
  },

  extraReducers: builder => {
    builder.addCase(createKeywords.pending, state => {
      state.isSubmitting = true
    })

    builder.addCase(createKeywords.fulfilled, state => {
      state.isSubmitting = false
    })

    builder.addCase(createKeywords.rejected, state => {
      state.isSubmitting = false
    })

    builder.addCase(fetchRecommendKeywords.pending, state => {
      state.recommends.isLoading = true
    })

    builder.addCase(fetchRecommendKeywords.fulfilled, (state, { payload }) => {
      state.recommends.content = payload.data
      state.recommends.isLoading = false
    })

    builder.addCase(fetchRecommendKeywords.rejected, state => {
      state.recommends.content = []
      state.recommends.isLoading = false
    })

    builder.addCase(
      fetchBiddingAverages.pending,
      (
        state,
        {
          meta: {
            arg: { keywords, rank, useRankAmount, rankAmount },
          },
        }
      ) => {
        state.biddingAverages.loaded = false
        state.biddingAverages.keywords = keywords
        state.biddingAverages.rank = rank
        state.biddingAverages.useRankAmount = useRankAmount
        state.biddingAverages.rankAmount = rankAmount
        state.biddingAverages.isLoading = true
        state.biddingAverages.visible = true
      }
    )

    builder.addCase(
      fetchBiddingAverages.fulfilled,
      (state, { payload: { results } }) => {
        state.biddingAverages.content = results
        state.biddingAverages.isLoading = false
        state.biddingAverages.loaded = true
      }
    )

    builder.addCase(fetchBiddingAverages.rejected, state => {
      state.biddingAverages.isLoading = false
    })

    builder.addCase(fetchChannelName.pending, state => {
      state.isChannelNamesLoading = true
    })

    builder.addCase(
      fetchChannelName.fulfilled,
      (
        state,
        {
          payload: {
            talkChannel: { name },
          },
        }
      ) => {
        state.form.channelNames = [name]
        state.isChannelNamesLoading = false
      }
    )

    builder.addCase(fetchChannelName.rejected, state => {
      state.isChannelNamesLoading = false
    })
  },
})
const { actions, reducer } = keywordSlice
export const {
  reset,
  setTexts,
  setSearch,
  setBiddingStrategyType,
  setMaxBiddingAmount,
  setMaxBiddingAmountChangeType,
  setRank,
  setUseRankAmount,
  setRankAmount,
  hideBiddingAverages,
  clear,
  addSuggestions,
  removeSuggestions,
  checkRecommends,
  uncheckRecommends,
  setChannelNames,
  setBrandNames,
  setProductNames,
  createCombinations,
  checkCombinations,
  uncheckCombinations,
  sort,
} = actions
export default reducer
